import React, { useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames/bind'
import { Trans, useTranslation } from 'react-i18next'
import styles from './PolePriceAudit.module.scss'
import { POLE_PRICE_AUDITS } from './PolePriceAuditsQuery'
import { useQuery, useReactiveVar } from '@apollo/client'
import { ImageAsset } from 'components/common/image-asset'
import { images, fallback } from 'images/image-library'
import { useHistory, useLocation } from "react-router-dom"
import { ReactComponent as LoadingIcon } from 'images/common/loading.svg'
import moment from 'moment'
import { useAuth } from 'config/context/authContext'
import { fuelGradesVar, stationsVar, statusesVar, periodVar } from 'config/apollo/pole-price-audit'
import { useStations } from 'components/common/hooks/useStations'
import { useStationGroups } from 'components/analytics/context/StationGroupsContext'
import { useFuelGrades } from 'components/common/hooks/useFuelGrades'
import { Filters } from './components/filters'
import { Copy } from 'components/common/copy'
import { Overlay, Tooltip } from 'react-bootstrap'
import Modal from 'react-bootstrap/Modal'
import { ReactComponent as InfoIcon } from 'images/common/info.svg'
import { getPriceChangeStatusesForAGivenLocale } from './components/status-dropdown/constants'
import { ExportButton } from './components/export-button'
import { createStationsInputFromStationsAndGroups } from 'utils/stationsAndGroups'
import { useDashboardViewed } from 'config/posthog/useDashboardViewed'
import { ReportNames } from 'config/posthog/constants'
import { useCompanyTimeZone } from 'components/common/hooks/useCompanyTimeZone'
import { createDateRangeFromNow, dateToUtcDayBoundaryString } from 'utils/dateTime'
import Pagination from 'components/common/pagination'
import { WidgetFallback } from 'components/common/widget-fallback'
import { withEdgeErrorBoundary } from 'components/common/with-edge-error-boundary'
import { useErrorHandler } from 'react-error-boundary'
import { NoData } from 'components/common/no-data'

const FUEL_FINDER_REPORTING_URL='https://www.gov.uk/guidance/report-your-fuel-prices-and-forecourt-details'

const cx = classNames.bind(styles)

const PolePriceAudit = () => {
  const location = useLocation()
  const history = useHistory()
  const query = new URLSearchParams(location.search)
  const pageParam = query.get("page")
  const page = pageParam && !isNaN(pageParam) && pageParam > 0 ? parseInt(pageParam) : 1

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(location.search)
    params.set('page', newPage)
    history.push(`${location.pathname}?${params.toString()}`)
  }

  const resetPageToFirst = () => {
    const params = new URLSearchParams(location.search)
    params.set('page', '1')
    history.push(`${location.pathname}?${params.toString()}`)
  }

  const { t } = useTranslation(['translations'])

  const { userLocale } = useAuth()
  const { companyTimeZone } = useCompanyTimeZone()
  const dateFormatString = userLocale === 'en-US' ? 'MM-DD-YYYY' : 'DD-MM-YYYY'
  const timeFormatString = 'HH:mm'
  const requiresCmaReporting = companyTimeZone === 'Europe/London'

  const allStatuses = useMemo(() => getPriceChangeStatusesForAGivenLocale(requiresCmaReporting, t), [requiresCmaReporting, t])
  const statusModalColumns = useMemo(() => {
    if (!requiresCmaReporting) {
      return [
        {
          heading: 'Status Key',
          statuses: allStatuses,
        },
      ]
    }

    return [
      {
        heading: 'No Action Required',
        statuses: allStatuses.filter(status => status.category === 'no-action'),
      },
      {
        heading: 'Potential Action Required',
        statuses: allStatuses.filter(status => status.category === 'potential-action'),
      },
      {
        heading: 'Immediate Action Required',
        statuses: allStatuses.filter(status => status.category === 'immediate-action'),
      },
    ]
  }, [allStatuses, requiresCmaReporting])

  const statusModalRows = useMemo(() => {
    // Get the max amount of statuses within a category (i.e no-action, potential-action or immediate-action)
    const maxStatuses = Math.max(
      ...statusModalColumns.map(column => column.statuses.length),
      0
    )

    // Return an array of rows, with each row containing a status from each category (i.e no-action, potential-action or immediate-action) or null if undefined
    // We can then use this to correctly order the statuses displayed within the modal, ensuring statuses between columns are aligned
    return Array.from({ length: maxStatuses }, (_, rowIndex) =>
      statusModalColumns.map(column => column.statuses[rowIndex] ?? null)
    )
  }, [statusModalColumns])

  const [showStatusInfoModal, setShowStatusInfoModal] = useState(false)
  const [activeStatusTooltip, setActiveStatusTooltip] = useState(null)
  const statusTooltipHideTimeoutRef = useRef(null)
  const statusHeaderText = t('widgets.polePriceAudit.logHeadersStatus')

  const selectedStationsAndGroupsIds = useReactiveVar(stationsVar)
  const { stations } = useStations()

  const { groups, loading: groupsLoading } = useStationGroups()

  const selectedFuelGrades = useReactiveVar(fuelGradesVar)
  const { fuelGrades: allFuelGrades, fuelGradesLoading } = useFuelGrades(selectedStationsAndGroupsIds, null, false, groups)

  const selectedPeriod = useReactiveVar(periodVar)

  const selectedStatuses = useReactiveVar(statusesVar)

  const setSelectedStations = (stationIds) => {
    resetPageToFirst()
    stationsVar(stationIds)
  }

  const setSelectedGrades = (grades) => {
    resetPageToFirst()
    const validGrades = grades.filter(grade => allFuelGrades.map(fg => fg.title).includes(grade))
    fuelGradesVar(validGrades)
  }

  const setSelectedPeriod = SelectedPeriod => {
    resetPageToFirst()
    periodVar(SelectedPeriod)
  }

  const setSelectedStatuses = (status) => {
    resetPageToFirst()
    statusesVar(status)
  }

  useEffect(() => {
    const stationIds = stations.map(station => station.id)
    stationsVar(stationIds)
  }, [stations])

  useEffect(() => {
    if (!fuelGradesLoading && allFuelGrades && allFuelGrades.length !== 0) {
      const selectedGrades = selectedFuelGrades.length ? selectedFuelGrades : allFuelGrades.map(fg => fg.title)
      setSelectedGrades(selectedGrades)
    }
  }, [fuelGradesLoading, allFuelGrades])

  useEffect(() => {
    statusesVar(allStatuses.map(item => item.key))
  }, [allStatuses])

  // Status tooltip cleanup
  // I.e on component unmount, clear any pending timeouts if they exist
  useEffect(() => {
    return () => {
      if (statusTooltipHideTimeoutRef.current) clearTimeout(statusTooltipHideTimeoutRef.current)
    }
  }, [])

  const stationIds = createStationsInputFromStationsAndGroups(groups, selectedStationsAndGroupsIds)

  // Convert selectedFuelGrades (array of titles) to an array of IDs as expected by the backend
  const fuelGradeIds = selectedFuelGrades
    .map(title => allFuelGrades?.find(fuelGrade => fuelGrade.title === title)?.id)
    .filter(id => id !== undefined)

  const defaultPeriod = createDateRangeFromNow(-30, 0, companyTimeZone)
  const periodFromDate = selectedPeriod?.from || defaultPeriod.from
  const periodToDate = selectedPeriod?.to || defaultPeriod.to
  const periodShown = { from: periodFromDate, to: periodToDate }

  useDashboardViewed(ReportNames.POLE_PRICE_AUDIT, periodShown)

  const { data, error, loading } = useQuery(POLE_PRICE_AUDITS, {
    notifyOnNetworkStatusChange: true,
    variables: {
      stationIds: stationIds,
      fuelGradeIds: fuelGradeIds,
      periodFrom: dateToUtcDayBoundaryString(companyTimeZone, periodFromDate),
      periodTo: dateToUtcDayBoundaryString(companyTimeZone, periodToDate, true),
      statuses: selectedStatuses.length === allStatuses.length ? [] : selectedStatuses,
      page: page,
    },
    fetchPolicy: 'network-only',
    skip: groupsLoading || fuelGradesLoading || !selectedStationsAndGroupsIds.length || !selectedFuelGrades?.length || !page,
  })

  useErrorHandler(error)

  // Show loading when dependencies are loading or when the query itself is loading
  const isLoading = (groupsLoading || fuelGradesLoading) || loading

  const tableHeaders = [
    t('widgets.polePriceAudit.logHeadersStation'),
    t('widgets.polePriceAudit.logHeadersFuelGrade'),
    t('widgets.polePriceAudit.logHeadersPriceChange'),
    t('widgets.polePriceAudit.logHeadersRequestedBy'),
    t('widgets.polePriceAudit.logHeadersChangeAt'),
    t('widgets.polePriceAudit.logHeadersDetectedAt'),
    requiresCmaReporting && t('widgets.polePriceAudit.logHeadersReportedAt'),
    t('widgets.polePriceAudit.logHeadersStatus'),
  ]

  const filterByEvent = (events, eventType) => {
    return events.find(
      (event) => event.type === eventType
    )
  }

  const renderStation = (station) => {
    return (
      <div className={cx('station')}>
        <ImageAsset
          height={40}
          width={40}
          src={images.brandImage(station.brand?.key)}
          alt={`station-cell-brand-${station.brand?.key}`}
          fallbackImage={fallback.defaultBrand}
        />
        <span className={cx('station-name')}>{station.name}</span>
      </div>
    )
  }

  const renderFuelGrade = (fuelGrade) => {
    return (
      <div className={cx('fuel-grade')}>
        <span
          className={cx('fuel-grade-colour')}
          style={{ backgroundColor: fuelGrade.primaryColour }}
        ></span>
        <span>{fuelGrade.name}</span>
      </div>
    )
  }

  const renderPriceChange = (oldPrice, newPrice) => {
    // Handle case where oldPrice doesn't exist (i.e. first pole price record)
    if (!oldPrice) {
      return (
        <div className={cx('price-change')}>
          <div className={cx('price-change-values')}>{newPrice}</div>
        </div>
      )
    }

    const priceChange = (Number(newPrice) - Number(oldPrice)).toFixed(2)
    const isIncrease = priceChange > 0
    const symbol = isIncrease ? '+' : ''

    return (
      <div className={cx('price-change')}>
        <div className={cx('price-change-values')}>{oldPrice} &#8594; {newPrice}</div>
        <div>
          <span>(</span>
          <span className={cx({ increase: isIncrease, decrease: !isIncrease })}>
            {symbol}{priceChange}
          </span>
          <span>)</span>
        </div>
      </div>
    )
  }

  const renderRequestedBy = (events) => {
    const priceChangeRequested = filterByEvent(events, 'price_change_requested')

    if (!priceChangeRequested) return '-'

    return (
      <span>{priceChangeRequested?.user?.name ?? '-'}</span>
    )
  }

  const renderEventDateAt = (events, eventType) => {
    const event = filterByEvent(events, eventType)

    if (!event) return '-'

    return (
      <div className={cx('event-date')}>
        <div>{moment.utc(event?.eventAt).tz(companyTimeZone).format(dateFormatString)}</div>
        <div>{moment.utc(event?.eventAt).tz(companyTimeZone).format(timeFormatString)}</div>
        {
          event?.cmaResponse?.reference &&
            <div className={cx('cma-response')}>
              <div className={cx('cma-response-reference')}><b>Ref:</b> {event?.cmaResponse?.reference}</div>
              <div><Copy textToCopy={event?.cmaResponse?.reference} tooltipText={event?.cmaResponse?.reference} /></div>
            </div>
        }
      </div>
    )
  }

  const renderStatusIcon = (status) => {
    return (
      <div className={cx('status-icon')}>
        <ImageAsset
          height={24}
          width={24}
          src={images.polePriceAuditStatuses(status)}
          alt={`status-icon-${status}`}
        />
      </div>
    )
  }

  const renderHeaderWithLineBreaks = (headerText) => {
    const headerLines = headerText.split('\n')

    return headerLines.map((headerLine, lineIndex) => (
      <span key={`${headerLine}-${lineIndex}`}>
        {headerLine}
        {lineIndex < headerLines.length - 1 && <br />}

        {
          headerText === statusHeaderText &&
            <button
              type="button"
              className={cx('status-info-button')}
              data-testid="status-info-button"
              onClick={() => setShowStatusInfoModal(true)}
              aria-label={statusHeaderText}
            >
              <InfoIcon height={17} width={17} data-testid="info-icon" />
            </button>
        }
      </span>
    ))
  }

  const renderStatusMessage = (status) => {
    if (!status?.messageKey) return status?.name

    return (
      <Trans
        i18nKey={status.messageKey}
        components={[
          <a
            key="fuel-finder-link"
            className={cx('status-message-link')}
            href={FUEL_FINDER_REPORTING_URL}
            target="_blank"
            rel="noopener noreferrer"
          />,
        ]}
      />
    )
  }

  const clearStatusTooltipHideTimeout = () => {
    if (statusTooltipHideTimeoutRef.current) {
      clearTimeout(statusTooltipHideTimeoutRef.current)
      statusTooltipHideTimeoutRef.current = null
    }
  }

  const handleStatusIconMouseEnter = (statusKey, event) => {
    clearStatusTooltipHideTimeout()
    setActiveStatusTooltip({
      statusKey,
      target: event.currentTarget,
    })
  }

  const handleStatusTooltipMouseEnter = () => {
    clearStatusTooltipHideTimeout()
  }

  const handleStatusTooltipMouseLeave = () => {
    clearStatusTooltipHideTimeout()
    statusTooltipHideTimeoutRef.current = setTimeout(() => {
      setActiveStatusTooltip(null)
      statusTooltipHideTimeoutRef.current = null
    }, 150)
  }

  const renderStatusModal = () => {
    return (
      <Modal
        className={cx('status-modal')}
        show={showStatusInfoModal}
        onHide={() => setShowStatusInfoModal(false)}
        centered
      >
        <Modal.Header className={cx('modal-header')} closeButton></Modal.Header>
        <Modal.Body className={cx('modal-body')}>
          <div className={cx('content')} data-testid="status-info-modal">
            <div className={cx('status-grid', { 'cma-columns': requiresCmaReporting })}>
              {statusModalColumns.map(column => (
                <div key={column.heading} className={cx('status-heading')}>
                  <div className={cx('label', { 'center': requiresCmaReporting })}><b>{column.heading}</b></div>
                </div>
              ))}
              {statusModalRows.map((row, rowIndex) =>
                row.map((status, columnIndex) => (
                  <div
                    key={`${status?.key ?? 'empty-cell'}-${rowIndex}-${columnIndex}`}
                  >
                    {status && (
                      <div className={cx('status')}>
                        <div>{renderStatusIcon(status.key)}</div>
                        <div>{renderStatusMessage(status)}</div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </Modal.Body>
      </Modal>
    )
  }

  const statusOverlay = (statusKey) => {
    const status = allStatuses.find(status => status.key === statusKey)

    return (
      <Tooltip
        className={cx('tooltip')}
        onMouseEnter={handleStatusTooltipMouseEnter}
        onMouseLeave={handleStatusTooltipMouseLeave}
      >
        <div className={cx('tooltip-content')} data-testid="tooltip">
          {renderStatusMessage(status)}
        </div>
      </Tooltip>
    )
  }

  return (
    <div className={cx('container')}>
      <div className={cx('header')}>
        <span className={cx('title')}>
          {(t('widgets.polePriceAudit.title'))}
        </span>
        <div>
          <ExportButton data={data?.polePriceAudits?.polePriceAudits || []} loading={isLoading} period={periodShown} />
        </div>
      </div>
      <div className={cx('filters')}>
        <Filters
          stationIds={selectedStationsAndGroupsIds}
          setSelectedStation={setSelectedStations}
          fuelGradesItems={allFuelGrades?.map((fg) => fg.title)}
          fuelGrades={selectedFuelGrades}
          setSelectedGrades={setSelectedGrades}
          statuses={selectedStatuses}
          setSelectedStatuses={setSelectedStatuses}
          period={selectedPeriod}
          setSelectedPeriod={setSelectedPeriod}
        />
      </div>
      <div className={cx('table')}>
        <table>
          <thead>
            <tr>
              {tableHeaders.map((header) => {
                if(!header) return // Filters out headers not required for US customers

                return (
                  <th key={header} className={cx('header-cell', {
                    'station-header-cell': header === 'Station',
                    'price-change': header.includes('Price change'),
                    'requested-by': header === 'Requested by',
                    'change-at': header === 'Change at',
                    'detected-at': header === 'Detected at',
                  })}>
                    {renderHeaderWithLineBreaks(header)}
                  </th>
                )
              })}
            </tr>
          </thead>
          {!isLoading && <tbody>
            {data?.polePriceAudits?.polePriceAudits.map((polePriceAudit) => (
              <tr key={polePriceAudit.id}>
                <td className={cx('station-body-cell')}>{renderStation(polePriceAudit.station)}</td>
                <td className={cx('fuel-grade-body-cell')}>{renderFuelGrade(polePriceAudit.fuelGrade)}</td>
                <td>{renderPriceChange(polePriceAudit.oldPrice, polePriceAudit.newPrice)}</td>
                <td className={cx('requested-by-body-cell', 'ph-no-capture')}>{renderRequestedBy(polePriceAudit.events)}</td>
                <td>{renderEventDateAt(polePriceAudit.events, 'price_change_requested')}</td>
                <td>{renderEventDateAt(polePriceAudit.events, 'price_change_detected')}</td>
                {requiresCmaReporting && <td>{renderEventDateAt(polePriceAudit.events, 'price_change_reported_to_cma')}</td>}
                <td>
                  <div
                    className={cx('status-tooltip-trigger')}
                    data-testid="status-tooltip-trigger"
                    onMouseEnter={(event) => handleStatusIconMouseEnter(polePriceAudit.status, event)}
                    onMouseLeave={handleStatusTooltipMouseLeave}
                  >
                    {renderStatusIcon(polePriceAudit.status)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>}
        </table>
        {!isLoading && (data?.polePriceAudits?.polePriceAudits.length === 0 || selectedStationsAndGroupsIds.length === 0) && <NoData noDataText={t('widgets.polePriceAudit.noData')} />}
        {isLoading && <div className={cx('loading')} data-testid="loading">
          <LoadingIcon height={100} width={100} />
        </div>}
      </div>

      {!isLoading && data?.polePriceAudits?.pageInfo?.totalPages > 1 &&
        <Pagination
          currentPage={page}
          totalPages={data.polePriceAudits?.pageInfo?.totalPages}
          onPageChange={handlePageChange}
          maxPagesToDisplay={5}
        />
      }

      {renderStatusModal()}

      <Overlay
        placement="top"
        show={!!activeStatusTooltip}
        target={activeStatusTooltip?.target}
      >
        {statusOverlay(activeStatusTooltip?.statusKey)}
      </Overlay>
    </div>
  )
}

const FallbackComponent = () => {
  const { t } = useTranslation(['translations'])

  return (
    <WidgetFallback
      title={t('widgets.polePriceAudit.title')}
    />
  )
}

const PolePriceAuditWithErrorBoundary = withEdgeErrorBoundary(
  PolePriceAudit,
  FallbackComponent,
  'PolePriceAudit',
)

export { PolePriceAuditWithErrorBoundary as PolePriceAudit }
